import { toWSDateString } from "@wingspanhq/fe-component-library";
import {
  IPayableSchema,
  IPayrollSettings,
  IScheduleDate
} from "@wingspanhq/payments/dist/interfaces";
import startCase from "lodash/startCase";
import { useCallback, useState } from "react";
import { getMatchingPayroll } from "../../../Invoices/utils/payables";
import { getPayeeNames } from "../../../modules/Payees/selectors/getPayeeNames";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryPayrollSettings } from "../../../query/payments/queries";
import { fetchAllPayeeRowsByIds } from "../../../query/search/payee/queries/useQueryAllPayeeRowsByIds";
import {
  listPayables,
  ListPayablesRequest,
  Payable
} from "../../../services/api/payments/payables";
import { IPayeeRow } from "../../../services/search";
import { useXWingspanExpansion } from "../../../shared/hooks/useXWingspanExpansion";

type PayableLineItem = NonNullable<Payable["lineItems"]>[number];

export type UseDownloadPayablesCsvArgs = {
  request: Omit<
    ListPayablesRequest,
    "page[mode]" | "page[size]" | "page[token]" | "page[resultSetId]"
  >;
};

const PAGE_SIZE = 100;
const MAX_PAGES = 200;

type PayeeNames = ReturnType<typeof getPayeeNames>;

type CsvContext = {
  payable: Payable;
  payeeRow?: IPayeeRow;
  names?: PayeeNames;
  payrollRun?: IScheduleDate;
  timezone: string;
};

// For pre-funded payroll the backend stamps `depositedAt` with the processing
// date rather than the contractor's expected pay date. When the stamp lands
// before the payable's `dueDate` (the expected pay date) we substitute
// `dueDate`. Instant payouts are exempt — their event timestamps are the real
// settlement times. Mirrors the legacy payables CSV report.
const selectorReportDepositedDate = (payable: Payable): Date | undefined => {
  if (payable.events?.instantPayoutAt) {
    return payable.events.instantPayoutAt;
  }

  const depositedDate = payable.events?.depositedAt;

  if (
    depositedDate &&
    payable.dueDate &&
    depositedDate.getTime() < payable.dueDate.getTime()
  ) {
    return payable.dueDate;
  }

  return depositedDate;
};

const selectorReportEstimatedDepositDate = (
  payable: Payable
): Date | undefined => {
  if (payable.events?.instantPayoutAt) {
    return payable.events?.estimatedDepositAt;
  }

  const estimatedDate = payable.events?.estimatedDepositAt;

  if (
    estimatedDate &&
    payable.dueDate &&
    estimatedDate.getTime() < payable.dueDate.getTime()
  ) {
    return payable.dueDate;
  }

  return estimatedDate;
};

// The matched `payrollRun` describes the *upcoming* run this payable is slated
// for. Once a payable has been paid that date is historical, so we fall back to
// the payable's own deposit/due dates. Mirrors the legacy payables CSV report.
const selectorReportPayrollDepositDate = (
  payable: Payable,
  payrollRun?: IScheduleDate
): Date | undefined => {
  if (payable.events?.paidAt) {
    return (
      payable.events?.depositedAt ||
      payable.events?.estimatedDepositAt ||
      payable.dueDate
    );
  }

  return payrollRun?.date;
};

// `getMatchingPayroll` only reads the effective due date (`client.payDate ||
// dueDate`), so a minimal shim lets us reuse the tested matching logic against
// the v2 payable shape without pulling in the whole `IPayableSchema`.
const toPayrollMatchInput = (payable: Payable): IPayableSchema =>
  ({
    client: { payDate: payable.payer?.payDate },
    dueDate: payable.dueDate
  }) as unknown as IPayableSchema;

const formatDate = (
  date: Date | undefined,
  format?: Parameters<typeof toWSDateString>[1]
): string | undefined => (date ? toWSDateString(date, format) : undefined);

const calculateLineItemRegularPrice = (
  lineItem: PayableLineItem
): number | undefined =>
  lineItem.unit
    ? (lineItem.costPerUnit ?? 0) * (lineItem.quantity ?? 0)
    : lineItem.totalCost;

const joinLineItemField = (
  map: (lineItem: PayableLineItem, index: number) => unknown,
  lineItems: PayableLineItem[] | undefined
): string | undefined => {
  if (!lineItems || lineItems.length === 0) {
    return undefined;
  }

  const mapped = lineItems
    .map(map)
    .filter(value => value !== undefined && value !== "" && value !== null);

  return mapped.length ? mapped.join(", ") : undefined;
};

// Per-line-item custom fields expand into one dynamic column each, keyed by
// `Line Item {n}. {Label}` — matching the legacy report so the same custom
// fields keep landing in the same columns.
const getLineItemLabelCells = (payable: Payable): Record<string, string> => {
  const cells: Record<string, string> = {};

  payable.lineItems?.forEach((lineItem, index) => {
    if (!lineItem.labels) {
      return;
    }

    Object.keys(lineItem.labels).forEach(labelKey => {
      const header = `Line Item ${index + 1}. ${startCase(labelKey)}`;
      cells[header] = lineItem.labels![labelKey];
    });
  });

  return cells;
};

type CsvColumn = {
  header: string;
  get: (ctx: CsvContext) => unknown;
};

// Static column set mirrors the legacy payables CSV report so downstream
// reports keep the fields customers rely on. Fields the v2 payables API does
// not expose (payable-level custom field labels, invoice type/project name)
// are omitted. Per-line-item custom fields are appended as dynamic columns.
const CSV_COLUMNS: CsvColumn[] = [
  { header: "Payable ID", get: ({ payable }) => payable.payableId },
  { header: "Payable Number", get: ({ payable }) => payable.payableNumber },
  {
    header: "External Contractor ID",
    get: ({ payeeRow }) => payeeRow?.payerOwnedData.payeeExternalId
  },
  { header: "Contractor Name", get: ({ names }) => names?.getResolvedName() },
  {
    header: "Contractor First Name",
    get: ({ payeeRow }) => payeeRow?.user.profile?.firstName
  },
  {
    header: "Contractor Last Name",
    get: ({ payeeRow }) => payeeRow?.user.profile?.lastName
  },
  {
    header: "Contractor Preferred Name",
    get: ({ payeeRow }) => payeeRow?.user.profile?.preferredName
  },
  { header: "Contractor Company Name", get: ({ names }) => names?.companyName },
  {
    header: "Contractor Email",
    get: ({ payeeRow }) => payeeRow?.user.email
  },
  { header: "Payee ID", get: ({ payable }) => payable.payeeId },
  { header: "Payer ID", get: ({ payable }) => payable.payerId },
  { header: "Amount", get: ({ payable }) => payable.amount },
  { header: "Currency", get: ({ payable }) => payable.currency?.toUpperCase() },
  {
    header: "Purchase Order Number",
    get: ({ payable }) => payable.metadata?.purchaseOrderNumber
  },
  {
    header: "Due Date",
    get: ({ payable }) => formatDate(payable.dueDate, "input")
  },
  {
    header: "Check Date",
    get: ({ payable }) => formatDate(payable.payer?.payDate, "input")
  },
  {
    header: "Payroll Deposit Date",
    get: ({ payable, payrollRun }) =>
      formatDate(selectorReportPayrollDepositDate(payable, payrollRun), "input")
  },
  {
    header: "Sent Date",
    get: ({ payable }) => formatDate(payable.events?.sentAt)
  },
  {
    header: "Opened Date",
    get: ({ payable }) => formatDate(payable.events?.openedAt)
  },
  {
    header: "Member Disputed Date",
    get: ({ payable }) => formatDate(payable.events?.disputedAt)
  },
  {
    header: "Invoice Declined Date",
    get: ({ payable }) => formatDate(payable.events?.payerDeclinedAt)
  },
  {
    header: "Approved Date",
    get: ({ payable }) => formatDate(payable.events?.approvedAt)
  },
  {
    header: "Paid Date",
    get: ({ payable }) => formatDate(payable.events?.paidAt)
  },
  {
    header: "Deposited Date",
    get: ({ payable }) => formatDate(selectorReportDepositedDate(payable))
  },
  {
    header: "Estimated Deposit Date",
    get: ({ payable }) =>
      formatDate(selectorReportEstimatedDepositDate(payable))
  },
  {
    header: "Cancelled Date",
    get: ({ payable }) => formatDate(payable.events?.cancelledAt)
  },
  { header: "Timezone", get: ({ timezone }) => timezone },
  {
    header: "Member Workflow Status",
    get: ({ payable }) => payable.payee?.workflowStatus
  },
  {
    header: "Client Workflow Status",
    get: ({ payable }) => payable.payer?.workflowStatus
  },
  { header: "Status", get: ({ payable }) => payable.status },
  {
    header: "Invoice PDF",
    get: ({ payable }) => payable.attachments?.payablePdf
  },
  {
    header: "Receipt PDF",
    get: ({ payable }) => payable.attachments?.receiptPdf
  },
  {
    header: "Invoice Link",
    get: ({ payable }) => payable.attachments?.payableLink
  },
  { header: "Notes", get: ({ payable }) => payable.payableNotes },
  { header: "Member Comment", get: ({ payable }) => payable.payee?.comment },
  {
    header: "Line Item Index",
    get: ({ payable }) =>
      joinLineItemField((_lineItem, index) => index, payable.lineItems)
  },
  {
    header: "Line Item Title",
    get: ({ payable }) =>
      joinLineItemField(lineItem => lineItem.description, payable.lineItems)
  },
  {
    header: "Line Item Description",
    get: ({ payable }) =>
      joinLineItemField(lineItem => lineItem.detail, payable.lineItems)
  },
  {
    header: "Line Item Amount",
    get: ({ payable }) =>
      joinLineItemField(
        lineItem => calculateLineItemRegularPrice(lineItem)?.toFixed(2),
        payable.lineItems
      )
  }
];

const escapeCell = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '""';
  }
  const stringified = String(value)
    .replace(/"/g, '""')
    .replace(/[\r\n]+/g, " ");
  return `"${stringified}"`;
};

type CsvRow = {
  ctx: CsvContext;
  lineItemLabelCells: Record<string, string>;
};

const toCsv = (
  payables: Payable[],
  payeesById: Map<string, IPayeeRow>,
  payrollSettings?: IPayrollSettings
): string => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const rows: CsvRow[] = payables.map(payable => {
    const payeeRow = payable.payeeId
      ? payeesById.get(payable.payeeId)
      : undefined;
    const names = payeeRow?.user ? getPayeeNames(payeeRow) : undefined;
    const payrollRun = payrollSettings
      ? getMatchingPayroll(payrollSettings, toPayrollMatchInput(payable))
      : undefined;

    return {
      ctx: { payable, payeeRow, names, payrollRun, timezone },
      lineItemLabelCells: getLineItemLabelCells(payable)
    };
  });

  // Collect the dynamic per-line-item label columns in first-seen order so a
  // custom field always lands in a stable column across the whole file.
  const dynamicHeaders: string[] = [];
  rows.forEach(row => {
    Object.keys(row.lineItemLabelCells).forEach(header => {
      if (!dynamicHeaders.includes(header)) {
        dynamicHeaders.push(header);
      }
    });
  });

  const headerRow = [
    ...CSV_COLUMNS.map(column => column.header),
    ...dynamicHeaders
  ]
    .map(escapeCell)
    .join(",");

  const dataRows = rows.map(row => {
    const staticCells = CSV_COLUMNS.map(column =>
      escapeCell(column.get(row.ctx))
    );
    const dynamicCells = dynamicHeaders.map(header =>
      escapeCell(row.lineItemLabelCells[header])
    );
    return [...staticCells, ...dynamicCells].join(",");
  });

  return [headerRow, ...dataRows].join("\n");
};

const triggerDownload = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const useDownloadPayablesCsv = (args: UseDownloadPayablesCsvArgs) => {
  const [isLoading, setIsLoading] = useState(false);
  const xWingspanExpansion = useXWingspanExpansion();
  const userId = useUserId();
  const payrollSettingsQuery = useQueryPayrollSettings(userId, {
    retry: false
  });
  const payrollSettings = payrollSettingsQuery.data;

  const download = useCallback(async () => {
    setIsLoading(true);
    try {
      const collected: Payable[] = [];
      let token = "";
      let pageCount = 0;

      while (pageCount < MAX_PAGES) {
        pageCount++;
        const page = await listPayables({
          ...args.request,
          "page[mode]": "cursor",
          "page[size]": PAGE_SIZE,
          ...(token ? { "page[token]": token } : {})
        });

        collected.push(...page.items);

        if (!page.nextPageToken) {
          break;
        }
        token = page.nextPageToken;
      }

      const payeeIds = collected
        .map(payable => payable.payeeId)
        .filter((id): id is string => Boolean(id));

      const payeeRows =
        payeeIds.length > 0
          ? await fetchAllPayeeRowsByIds(payeeIds, xWingspanExpansion)
          : [];

      const payeesById = new Map<string, IPayeeRow>(
        payeeRows.map(row => [row.payeeId, row])
      );

      const csv = toCsv(collected, payeesById, payrollSettings);
      const filename = `payables-${new Date().toISOString().slice(0, 10)}.csv`;
      triggerDownload(csv, filename);
    } finally {
      setIsLoading(false);
    }
  }, [args.request, xWingspanExpansion, payrollSettings]);

  return {
    download,
    isLoading,
    isDisabled: false
  };
};
